import {DocumentNode, gql} from '@apollo/client';
import {groupBy} from '@benchling/js-utils';

import {assert} from 'Asserts';
import {ProcedureParameterConfirmationMethod} from 'graphql-client/__generated/graphqlGlobalTypes';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {TypedValue} from 'notebook/tables/TableConstants';
import {sortNodesByCoordinates} from 'procedures/design/flowchartManagementUtils';
import {collapseTypedValuesForMultiValuePlateMap} from 'procedures/planning/multiValuePlateMap';
import {
  ExperimentalInputs,
  ExperimentalInputTypedValuesByComponentName,
} from 'procedures/planning/ProcedurePlanTypes';
import {getPowAgnosticTemplateFlowchartVersionFromProcedureRunPlan} from 'procedures/utils/powAgnosticFlowchartHelpers';
import {
  EXPERIMENTAL_INPUTS_FLOWCHART_FRAGMENT,
  ExperimentFlowchart,
  ExperimentMethod,
  getValuesForMethod,
  prepareInitialExperimentalInputs,
} from 'studies/typed/shared/ExperimentalInputs';
import {EXPERIMENTAL_INPUTS_PROCEDURE_FLOWCHART_VERSION_PROXY_FRAGMENT} from 'studies/typed/shared/ExperimentalInputs';

import {GetStudyDesignFromProcedureRunPlanGraphQL as ProcedureRunPlanNode} from './__generated/GetStudyDesignFromProcedureRunPlanGraphQL';

export interface StudyDesignParams {
  experimentalInputs: ExperimentalInputs;
  templateFlowchart: ExperimentFlowchart;
}

/**
 * Extracts the study design from a ProcedureRunPlanNode.
 *
 * @param procedureRunPlan - a ProcedureRunPlanNode that contains STUDY_DESIGN_FROM_PROCEDURE_RUN_PLAN_FRAGMENT
 * @returns an object of {experimentalInputs, templateFlowchart} for use with design components.
 */
export default function getStudyDesignFromProcedureRunPlan(
  procedureRunPlan: ProcedureRunPlanNode
): StudyDesignParams {
  const flowchartVersion = getPowAgnosticTemplateFlowchartVersionFromProcedureRunPlan(procedureRunPlan);
  assert(flowchartVersion != null, 'Expected flowchart version to be present');
  assert(!isInaccessible(flowchartVersion), 'Expected flowchart version to be accessible');

  const nodes = flowchartVersion.nodes;
  const templateMethods: Array<ExperimentMethod> = sortNodesByCoordinates(
    [...nodes],
    'gridLayoutCoordinates'
  );

  const templateFlowchart: ExperimentFlowchart = {
    ...flowchartVersion,
    nodes: templateMethods,
  };

  const sortedConditions = [...procedureRunPlan.procedureRunPlanConditions].sort(
    (a, b) => a.position - b.position
  );

  // When there are no stored conditions, default to 1 condition to match the component's fallback.
  // This ensures experimentalInputs.values has the same length as initialConditions.
  const numConditions = sortedConditions.length > 0 ? sortedConditions.length : 1;

  const confirmationMethodsByParamVersionId = new Map(
    procedureRunPlan.confirmationMethodByParamVersionId.map((cm) => [
      cm.paramVersionId,
      cm.confirmationMethod,
    ])
  );

  const prevConfirmationMethods = Object.fromEntries(
    templateMethods.map((methodNode) => [
      methodNode.id,
      {
        parameterConfirmationMethods: getConfirmationMethodsForMethod(
          methodNode,
          confirmationMethodsByParamVersionId
        ),
      },
    ])
  );

  // Build planValues from procedureRunPlanConditions.methodParametersPlans
  // For each parameter, if no value was stored (empty array), use the template default.
  // This ensures "No change" is shown correctly when values equal the default.
  const planValues = Object.fromEntries(
    templateMethods.map((methodNode) => {
      const defaultValues = getValuesForMethod(methodNode);
      return [
        methodNode.id,
        {
          values: sortedConditions.map((condition) => {
            const methodPlan = condition.methodParametersPlans.find(
              (mp) => mp.templateMethodVersion.id === methodNode.id
            );

            const loadedValues = getValuesFromMethodPlan(methodNode, methodPlan);
            return mergeValuesWithDefaults(loadedValues, defaultValues);
          }),
        },
      ];
    })
  );

  const experimentalInputs = prepareInitialExperimentalInputs(numConditions, templateMethods, {
    prevExperimentalInputValues: planValues,
    prevConfirmationMethods,
  });

  return {experimentalInputs, templateFlowchart};
}

type MethodPlan = ProcedureRunPlanNode['procedureRunPlanConditions'][number]['methodParametersPlans'][number];

/**
 * Merges loaded values with defaults at the parameter level.
 * For each parameter, if the loaded value is an empty array (no stored value), use the default instead.
 * This ensures "No change" is shown correctly when values equal the template default.
 */
function mergeValuesWithDefaults(
  loadedValues: ExperimentalInputTypedValuesByComponentName,
  defaultValues: ExperimentalInputTypedValuesByComponentName
): ExperimentalInputTypedValuesByComponentName {
  return {
    parameters: loadedValues.parameters.map((paramFields, paramIdx) =>
      paramFields.length > 0 ? paramFields : defaultValues.parameters[paramIdx]
    ),
    materialInputs: loadedValues.materialInputs.map((inputFields, inputIdx) =>
      inputFields.length > 0 ? inputFields : defaultValues.materialInputs[inputIdx]
    ),
    equipment: loadedValues.equipment.map((equipFields, equipIdx) =>
      equipFields.length > 0 ? equipFields : defaultValues.equipment[equipIdx]
    ),
  };
}

/**
 * Extracts parameter values from a method plan, matching them to the template method's parameter order.
 * Similar to getValuesForMethod but for plan stored values instead of PMEI planned values.
 *
 * Plan values are stored as JSONScalar with different shapes:
 * - Data parameters: { value: TypedValue }
 * - Material inputs: { value: { material: TypedValue, amount: TypedValue } }
 * - Equipment: { value: { equipment: TypedValue } }
 *
 * For plate-confirmed parameters with multiple setpoints, the plan may contain multiple stored
 * entries sharing a single `parameterId` / `materialInputId`. Those are collapsed back into a
 * single multi-value TypedValue (ARRAY or multi-link BIOENTITY_LINK) for the UI, matching the
 * shape `useInputTable` expects for `isMulti: true` columns.
 */
function getValuesFromMethodPlan(
  templateMethod: ExperimentMethod,
  methodPlan: MethodPlan | undefined
): ExperimentalInputTypedValuesByComponentName {
  if (methodPlan == null) {
    return {
      parameters: templateMethod.dataParameterSettings.map(() => []),
      materialInputs: templateMethod.materialInputSettings.map(() => []),
      equipment: templateMethod.equipmentSettings.map(() => []),
    };
  }

  const dataParamValuesById = groupBy(methodPlan.dataParameterValues, (r) => r.parameterId);
  const materialInputValuesById = groupBy(methodPlan.materialInputValues, (r) => r.materialInputId);
  const equipmentValuesById = groupBy(methodPlan.equipmentValues, (r) => r.equipmentId);

  return {
    // Data parameters: each stored entry's `value` field is { value: TypedValue } (the outer is
    // the GraphQL wrapper, the inner is the backend-stored wrapper); multiple entries collapse
    // into a single multi-value TypedValue for plate-confirmed multi-setpoint params.
    parameters: templateMethod.dataParameterSettings.map((setting) => {
      const storedEntries = dataParamValuesById[setting.version.id] ?? [];
      const perEntryValues = storedEntries.map(
        (row) => (row.value as Record<string, unknown> | undefined)?.value as TypedValue | undefined
      );
      const collapsed = collapseTypedValuesForMultiValuePlateMap(perEntryValues);
      return collapsed != null ? [collapsed] : [];
    }),

    // Material inputs: each stored entry's `value` field is { value: { material, amount } };
    // multiple entries collapse into a single multi-value material + a single amount (amount is
    // never multi-value — allowMultiValueForPlateMap is false for the amount field).
    materialInputs: templateMethod.materialInputSettings.map((setting) => {
      const storedEntries = materialInputValuesById[setting.version.id] ?? [];
      const innerValues = storedEntries.map(
        (row) =>
          (row.value as Record<string, unknown> | undefined)?.value as Record<string, unknown> | undefined
      );
      const perEntryMaterials = innerValues.map((iv) => iv?.material as TypedValue | undefined);
      const collapsedMaterial = collapseTypedValuesForMultiValuePlateMap(perEntryMaterials);
      // Invariant: the write path duplicates one amount across all expanded rows for a given
      // parameter, so every stored entry's `amount` must be identical. If that invariant were
      // ever violated (bad data / manual edit), we deterministically pick the first non-null.
      const amount = (innerValues.find((iv) => iv?.amount != null)?.amount as TypedValue | undefined) ?? null;
      if (collapsedMaterial == null && amount == null) {
        return [];
      }
      return [collapsedMaterial, amount] as Array<TypedValue>;
    }),

    // Equipment: each stored entry's `value` field is { value: { equipment: TypedValue } }.
    // Equipment parameters have allowMultiValueForPlateMap: false, so the plan should never hold
    // more than one stored entry per equipment id.
    equipment: templateMethod.equipmentSettings.map((setting) => {
      const storedEntries = equipmentValuesById[setting.version.id] ?? [];
      assert(
        storedEntries.length <= 1,
        `Expected at most one stored equipment entry for ${setting.version.id}, got ${storedEntries.length}`
      );
      const stored = storedEntries[0]?.value as Record<string, unknown> | undefined;
      const innerValue = stored?.value as Record<string, unknown> | undefined;
      const value = innerValue?.equipment as TypedValue | undefined;
      return value != null ? [value] : [];
    }),
  };
}

/**
 * Gets confirmation methods for a method's parameters based on the plan-level mapping.
 */
function getConfirmationMethodsForMethod(
  methodNode: ExperimentMethod,
  confirmationMethodsByParamVersionId: Map<string, ProcedureParameterConfirmationMethod>
): ExperimentalInputs[string]['parameterConfirmationMethods'] {
  return {
    parameters: methodNode.dataParameterSettings.map(
      (setting) =>
        confirmationMethodsByParamVersionId.get(setting.version.id) ?? setting.version.confirmationMethod
    ),
    materialInputs: methodNode.materialInputSettings.map(
      (setting) =>
        confirmationMethodsByParamVersionId.get(setting.version.id) ?? setting.version.confirmationMethod
    ),
    equipment: methodNode.equipmentSettings.map(
      (setting) =>
        confirmationMethodsByParamVersionId.get(setting.version.id) ?? setting.version.confirmationMethod
    ),
  };
}

export const STUDY_DESIGN_FROM_PROCEDURE_RUN_PLAN_FRAGMENT: DocumentNode = gql`
  fragment GetStudyDesignFromProcedureRunPlanGraphQL on ProcedureRunPlanNode {
    id
    description
    dataEntryMode
    templateProcedureFlowchartVersion {
      ... on ProcedureFlowchartVersionNode {
        id
        ...ExperimentalInputsFlowchartGraphQL
      }
      ...InaccessibleInfoGraphQL
    }
    procedureFlowchartConfigVersionProxy {
      ...ExperimentalInputsProcedureFlowchartVersionProxyGraphQL
      ...InaccessibleInfoGraphQL
    }
    procedureRunPlanConditions {
      id
      name
      description
      position
      methodParametersPlans {
        templateMethodVersion {
          id
        }
        dataParameterValues {
          parameterId
          value
        }
        materialInputValues {
          materialInputId
          value
        }
        equipmentValues {
          equipmentId
          value
        }
      }
    }
    confirmationMethodByParamVersionId {
      paramVersionId
      confirmationMethod
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
  ${EXPERIMENTAL_INPUTS_FLOWCHART_FRAGMENT}
  ${EXPERIMENTAL_INPUTS_PROCEDURE_FLOWCHART_VERSION_PROXY_FRAGMENT}
`;
